import { Stack, Typography, Button, useTheme } from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode } from "react";

/**
 * Interface de propriedades para o ExtensionCard
 */
interface ExtensionCardProps {
  /** Título do card */
  title: string;
  /** Descrição da extensão */
  description: string;
  /** Texto do botão de ação */
  buttonText: string;
  /** Função de clique do botão (opcional) */
  onButtonClick?: () => any;
  /** Status da extensão (em desenvolvimento, disponível, etc) */
  status?: string;
  /** Conteúdo adicional a ser mostrado no card */
  children?: ReactNode;
  /** Indica se o botão deve estar desabilitado */
  buttonDisabled?: boolean;
  /** Indica se o botão deve ser vermelho */
  isRedButton?: boolean;
  priceInfo?: string;
}

/**
 * Componente ExtensionCard
 * 
 * Este componente exibe um card padronizado para extensões do Fluxmed.
 * Permite personalização de título, descrição, botão e conteúdo adicional.
 */
export function ExtensionCard({
  title,
  description,
  buttonText,
  onButtonClick,
  status,
  children,
  buttonDisabled = false,
  isRedButton = false,
  priceInfo
}: ExtensionCardProps) {
  const theme = useTheme();
  // Função padrão para o botão caso não seja fornecida
  const handleButtonClick = onButtonClick || (() => console.log('Botão clicado'));
  
  // Desabilita o botão se explicitamente definido ou se não houver função de clique
  const isDisabled = buttonDisabled || !onButtonClick;

  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "neutral700.main",
        borderRadius: "15px",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        minHeight: "500px",
        paddingTop: 3,
        paddingBottom: 3
      }}
    >
      {status && (
        <Stack
          sx={{
            position: "absolute",
            top: "12px",
            right: "0px",
            bgcolor: status === "Em desenvolvimento" ? "warning.main" : "success.main",
            color: "white",
            padding: "4px 12px",
            borderTopLeftRadius: "15px",
            borderBottomLeftRadius: "15px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1
          }}
        >
          {status}
        </Stack>
      )}
      
      <Stack flexGrow={1}>
        <Typography
          sx={{
            fontSize: "1.25rem",
            fontWeight: "500",
            paddingBottom: 3,
            paddingLeft: 5,
            paddingRight: 5,
            paddingTop: 2,
            alignSelf: "center",
            textAlign: "center",
            position: "relative", 
            zIndex: 0
          }}
        >
          {title}
        </Typography>
        
        <Typography
          variant="body1"
          color="neutral700.main"
          sx={{
            paddingBottom: 3,
            paddingLeft: 5,
            paddingRight: 5
          }}
          align="left"
        >
          {description}
        </Typography>
        
        {children && (
          <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
            {children}
          </Stack>
        )}
      </Stack>
      
      <Stack 
        direction="row" 
        width="100%"
        justifyContent="center"
        marginTop="auto"
        paddingTop={2}
      >
        {isRedButton ? (
          <Button
            sx={{
              width: "60%",
              height: "40px",
              backgroundColor: "error700.main",
              color: "neutral0.main",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "error700.main" },
              textTransform: "capitalize",
              fontSize: theme.typography.subtitle2.fontSize,
            }}
            onClick={handleButtonClick}
            disabled={isDisabled}
          >
            {buttonText}
          </Button>
        ) : (
          <PrimaryButton
            width="60%"
            height="100%"
            onClick={handleButtonClick}
            disabled={isDisabled}
          >
            {buttonText}
          </PrimaryButton>
        )}
      </Stack>
    </Stack>
  );
} 